<script setup>
import { useModal } from 'vue-final-modal';
import BiForecastModal from '~/bi/components/chart-builder/common/bi-forecast-modal.vue';
import { DUAL_Y_AXIS_SUPPORTED_CHARTS } from '~/bi/constants/bi-constants';
import { useCommonImports } from '~/common/composables/common-imports.composable';

const props = defineProps({
  seriesConfig: {
    type: Object,
    required: true,
  },
  chartConfig: {
    type: Object,
    required: true,
  },
  columns: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['fieldSelected']);

const { $t } = useCommonImports();

const forecast_modal = useModal({
  component: BiForecastModal,
  attrs: {
    onClose() {
      forecast_modal.close();
    },
  },
});

const state = reactive({
  is_menu_open: false,
  force_update_legend: Number.MIN_SAFE_INTEGER,
  value: null,
  legend: null,
  type: null,
  color: null,
  y_axis: null,
  line_style: null,
  line_width: null,
  line_shape: null,
  prefix: null,
  suffix: null,
  stack: null,
  opacity: null,
});

const types = computed(() => {
  return [
    { uid: 'bar', leftSlot: h(IconHawkHorizontalBarChartOne, { style: 'transform: rotate(-90deg);' }), tooltip_text: $t('Bar') },
    { uid: 'line', leftSlot: IconHawkLineChartUpOne, tooltip_text: $t('Line') },
    { uid: 'area', leftSlot: IconHawkAreaChart, tooltip_text: $t('Area') },
  ];
});

const y_axis_options = computed(() => [
  { uid: 'primary', label: $t('Primary') },
  { uid: 'secondary', label: $t('Secondary') },
]);

const line_styles = computed(() => {
  return [
    { uid: 'solid', leftSlot: h('div', { style: 'height: 20px; width: 20px; display: flex; align-items: center; justify-content: center;' }, [h('hr', { style: 'border: none; height: 2px; background-color: currentColor; margin: 0; width: 100%;' })]), tooltip_text: $t('Solid') },
    { uid: 'dashed', leftSlot: IconHawkStroke, tooltip_text: $t('Dashed') },
    { uid: 'dotted', leftSlot: IconHawkDotsHorizontal, tooltip_text: $t('Dotted') },
  ];
});

const line_widths = computed(() => {
  return [
    { uid: 1, leftSlot: h('div', { style: 'height: 20px; width: 20px; display: flex; align-items: center; justify-content: center;' }, [h('hr', { style: 'border: none; height: 1px; background-color: currentColor; margin: 0; width: 100%;' })]), tooltip_text: $t('Thin') },
    { uid: 2, leftSlot: h('div', { style: 'height: 20px; width: 20px; display: flex; align-items: center; justify-content: center;' }, [h('hr', { style: 'border: none; height: 2px; background-color: currentColor; margin: 0; width: 100%;' })]), tooltip_text: $t('Medium') },
    { uid: 3, leftSlot: h('div', { style: 'height: 20px; width: 20px; display: flex; align-items: center; justify-content: center;' }, [h('hr', { style: 'border: none; height: 3px; background-color: currentColor; margin: 0; width: 100%;' })]), tooltip_text: $t('Thick') },
  ];
});

const line_shapes = computed(() => {
  return [
    { uid: 'straight', leftSlot: IconHawkSquareLine, tooltip_text: $t('Straight') },
    { uid: 'curved', leftSlot: IconHawkSmoothLine, tooltip_text: $t('Curved') },
  ];
});

function onFieldSelected(field_name, new_value, old_value) {
  if (field_name === 'value' && state.legend === old_value) {
    state.legend = new_value;
    emit('fieldSelected', { legend: new_value });
    state.force_update_legend += 1;
  }
  state[field_name] = new_value;
  emit('fieldSelected', { [field_name]: new_value });
}

function onMenuOpen() {
  Object.keys(props.seriesConfig).forEach((key) => {
    state[key] = props.seriesConfig[key];
  });
  state.is_menu_open = true;
}

function onOpenForecastModal() {
  forecast_modal.patchOptions({
    attrs: {
      mode: 'edit',
      initialData: props.seriesConfig.forecast_config,
      chartConfig: props.chartConfig,
      onSave(data) {
        onFieldSelected('forecast_config', data);
        forecast_modal.close();
      },
    },
  });
  forecast_modal.open();
}
</script>

<template>
  <div class="flex items-center gap-1">
    <HawkMenu
      position="fixed"
      additional_trigger_classes="mt-1.5 p-0 m-0 !ring-0 !border-0 focus:!ring-0"
      additional_dropdown_classes="ml-4"
      @open="onMenuOpen"
      @close="state.is_menu_open = false"
    >
      <template #trigger>
        <IconHawkDotsVertical class="w-4 h-4" />
      </template>
      <template #content>
        <div class="px-3.5 py-3">
          <div class="mb-3">
            <h3 class="text-lg font-semibold text-gray-900 mb-3">
              {{ $t('Configure series') }}
            </h3>
            <hr class="-mx-3.5">
          </div>

          <Vueform
            v-if="state.is_menu_open"
            size="sm"
            :columns="{
              default: { container: 12, label: 4, wrapper: 12 },
              sm: { container: 12, label: 4, wrapper: 12 },
              md: { container: 12, label: 4, wrapper: 12 },
            }"
            :add-classes="{
              FormElements: {
                container: '!gap-y-4',
              },
            }"
          >
            <div class="col-span-12">
              <div class="flex items-center gap-3">
                <BiColorPicker :active-color="state.color" @color-selected="onFieldSelected('color', $event)" />
                <div class="flex-1">
                  <div
                    v-if="props.seriesConfig?.forecast_config?.forecast_series_name"
                    class="flex items-center gap-3"
                  >
                    <span class="text-md font-semibold">
                      {{ props.seriesConfig.forecast_config.forecast_series_name }}
                    </span>
                    <HawkButton
                      v-tippy="{
                        content: $t('Edit forecast configuration'),
                        placement: 'top',
                      }"
                      icon
                      size="xs"
                      type="text"
                      @click="onOpenForecastModal"
                    >
                      <IconHawkSettingsOne />
                    </HawkButton>
                  </div>
                  <SelectElement
                    v-else
                    name="value"
                    :items="props.columns"
                    :native="false"
                    :can-clear="false"
                    :can-deselect="false"
                    :default="state.value"
                    :placeholder="$t('Select series')"
                    :add-classes="{
                      SelectElement: {
                        select: {
                          container: '!min-h-7 !h-7',
                        },
                      },
                    }"
                    @change="(newVal, oldVal) => onFieldSelected('value', newVal, oldVal)"
                  />
                </div>
              </div>
            </div>

            <TextElement
              :key="state.force_update_legend"
              name="legend"
              :label="$t('Name')"
              :default="state.legend"
              :placeholder="$t('Enter legend text')"
              :add-classes="{
                TextElement: {
                  inputContainer: '!h-7',
                },
              }"
              @change="onFieldSelected('legend', $event)"
              @keydown.space.stop
            />

            <div v-show="props.chartConfig?.type !== 'scatter_chart'" class="col-span-12">
              <div class="grid grid-cols-12 gap-0 items-center">
                <label class="col-span-4 text-sm font-medium text-gray-700">{{ $t('Chart type') }}</label>
                <div class="col-span-8">
                  <HawkButtonGroup
                    :items="types"
                    icon
                    size="sm"
                    :active_item="state.type"
                    class="w-fit"
                    @select="onFieldSelected('type', $event.uid)"
                  />
                </div>
              </div>
            </div>

            <div v-show="DUAL_Y_AXIS_SUPPORTED_CHARTS.includes(props.chartConfig?.type)" class="col-span-12">
              <div class="grid grid-cols-12 gap-0 items-center">
                <label class="col-span-4 text-sm font-medium text-gray-700">{{ $t('Y-axis') }}</label>
                <div class="col-span-8">
                  <HawkButtonGroup
                    :items="y_axis_options"
                    size="sm"
                    :active_item="state.y_axis"
                    class="w-fit"
                    @select="onFieldSelected('y_axis', $event.uid)"
                  />
                </div>
              </div>
            </div>

            <div v-show="props.chartConfig?.type !== 'scatter_chart'" class="col-span-12">
              <div class="grid grid-cols-12 gap-0 items-center">
                <label class="col-span-4 text-sm font-medium text-gray-700">{{ $t('Line style') }}</label>
                <div class="col-span-8">
                  <HawkButtonGroup
                    :items="line_styles"
                    icon
                    size="sm"
                    :active_item="state.line_style"
                    class="w-fit"
                    @select="onFieldSelected('line_style', $event.uid)"
                  />
                </div>
              </div>
            </div>

            <div v-show="props.chartConfig?.type !== 'scatter_chart'" class="col-span-12">
              <div class="grid grid-cols-12 gap-0 items-center">
                <label class="col-span-4 text-sm font-medium text-gray-700">{{ $t('Line width') }}</label>
                <div class="col-span-8">
                  <HawkButtonGroup
                    :items="line_widths"
                    icon
                    size="sm"
                    :active_item="state.line_width"
                    class="w-fit"
                    @select="onFieldSelected('line_width', $event.uid)"
                  />
                </div>
              </div>
            </div>

            <div v-show="props.chartConfig?.type !== 'scatter_chart'" class="col-span-12">
              <div class="grid grid-cols-12 gap-0 items-center">
                <label class="col-span-4 text-sm font-medium text-gray-700">{{ $t('Line shape') }}</label>
                <div class="col-span-8">
                  <HawkButtonGroup
                    :items="line_shapes"
                    icon
                    size="sm"
                    :active_item="state.line_shape"
                    class="w-fit"
                    @select="onFieldSelected('line_shape', $event.uid)"
                  />
                </div>
              </div>
            </div>

            <TextElement
              name="prefix"
              :label="$t('Prefix')"
              :default="state.prefix"
              :placeholder="$t('Enter prefix')"
              :add-classes="{
                TextElement: {
                  inputContainer: '!h-7',
                },
              }"
              @change="onFieldSelected('prefix', $event)"
              @keydown.space.stop
            />

            <TextElement
              name="suffix"
              :label="$t('Suffix')"
              :default="state.suffix"
              :placeholder="$t('Enter suffix')"
              :add-classes="{
                TextElement: {
                  inputContainer: '!h-7',
                },
              }"
              @change="onFieldSelected('suffix', $event)"
              @keydown.space.stop
            />

            <CheckboxElement
              v-if="props.chartConfig.stack_by && props.chartConfig.stack_by !== 'none'"
              name="stack"
              :default="state.stack"
              @change="onFieldSelected('stack', $event)"
            >
              <template #label>
                <div class="flex items-center gap-1">
                  <label class="text-sm font-medium text-gray-700">{{ $t('Stack') }}</label>
                  <IconHawkInfoCircle class="w-4 h-4 text-gray-400" />
                </div>
              </template>
            </CheckboxElement>

            <SliderElement
              v-if="state.type === 'bar'"
              name="opacity"
              :label="$t('Opacity')"
              :default="state.opacity"
              :min="0"
              :max="1"
              :step="0.01"
              :format="v => `${Math.round(v * 100)}%`"
              @change="onFieldSelected('opacity', $event)"
            />
          </Vueform>
        </div>
      </template>
    </HawkMenu>
  </div>
</template>
